import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCredits } from "@/hooks/useCreditBalance";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip } from "@/components/ui/tooltip-custom";

/**
 * Composant CreditBalanceCompact - Version compacte pour sidebar réduite
 * 
 * Version minimaliste du composant CreditBalance pour la sidebar en mode réduit.
 * Affiche uniquement le nombre de crédits et un bouton d'achat.
 * 
 * Requirements: 6.1, 6.2, 6.5
 */
interface CreditBalanceCompactProps {
  onBuyCredits: () => void;
  className?: string;
}

export const CreditBalanceCompact = ({ onBuyCredits, className = "" }: CreditBalanceCompactProps) => {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  
  // Hook avec mises à jour en temps réel (Requirements 6.2, 6.5)
  const { availableCredits, loading, error } = useCredits(organizationId || undefined);

  // État de chargement
  if (loading) {
    return (
      <div className={`flex overflow-hidden flex-col pt-4 pb-4 w-full ${className}`}>
        <div className="flex flex-col justify-center items-center w-full">
          <Skeleton className="h-5 w-8 bg-slate-600" />
          <Skeleton className="h-4 w-12 mt-1 bg-slate-600" />
        </div>
        <div className="flex justify-center items-center mt-2">
          <Skeleton className="w-8 h-8 rounded-lg bg-slate-600" />
        </div>
      </div>
    );
  }

  // Gestion d'erreur
  if (error) {
    return (
      <div className={`flex overflow-hidden flex-col pt-4 pb-4 w-full ${className}`}>
        <div className="flex flex-col justify-center items-center w-full text-white whitespace-nowrap">
          <div className="text-sm font-semibold text-red-400">
            --
          </div>
          <div className="mt-0.5 text-xs font-medium text-gray-300">
            Erreur
          </div>
        </div>
        <div className="flex justify-center items-center mt-2">
          <Tooltip content="Acheter des crédits" position="right">
            <Button
              size="sm"
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.06] bg-[hsl(var(--app-sidebar-surface))] p-0 text-white transition-all duration-200 hover:bg-white/[0.05] hover:scale-105"
              onClick={onBuyCredits}
              aria-label="Acheter des crédits"
            >
              <Plus className="h-4 w-4 text-[hsl(var(--primary))]" />
            </Button>
          </Tooltip>
        </div>
      </div>
    );
  }

  // Affichage normal (Requirement 6.1)
  return (
    <div className={`flex overflow-hidden flex-col pt-4 pb-4 w-full ${className}`}>
      <div className="flex flex-col justify-center items-center w-full text-white whitespace-nowrap">
        <div className="text-sm font-semibold text-white">
          {availableCredits}
        </div>
        <div className="mt-0.5 text-xs font-medium text-gray-300">
          Crédits
        </div>
      </div>
      <div className="flex justify-center items-center mt-2">
        <Tooltip content="Acheter des crédits" position="right">
          <Button
            size="sm"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/[0.06] bg-[hsl(var(--app-sidebar-surface))] p-0 text-white transition-all duration-200 hover:bg-white/[0.05] hover:scale-105"
            onClick={onBuyCredits}
            aria-label="Acheter des crédits"
          >
            <Plus className="h-4 w-4 text-[hsl(var(--primary))]" />
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
